"use client";

/**
 * CitationInfoCard - Shared citation metadata display used by both
 * CitationHoverCard header and CitationOption dropdown items.
 *
 * Displays: status badge, value, source line (filename, page, confidence
 * ring), cited text.
 */

import * as React from "react";
import { ShieldCheck, AlertTriangle, Sparkles, Globe } from "lucide-react";
import type { Citation } from "@quovy/field-types";
import { cn, safeHostname } from "../../utils";
import { citationStatusColors } from "./citation-helpers";
import { ConfidenceIndicatorRing } from "../../data-display/confidence-indicator";

export interface CitationInfoCardProps {
  /** Canonical citation: id, document_id, label, optional page/bbox/snippet. */
  citation: Citation;
  /** Display value for this citation (e.g. the field's extracted value). */
  value?: string;
  /** Filename to show in the footer; falls back to `citation.label`. */
  filename?: string;
  /** Page number; falls back to `citation.page`. */
  pageNumber?: number;
  /** Verbatim cited text; falls back to `citation.snippet`. */
  citedText?: string;
  /** Confidence score (0..1). */
  confidence?: number;
  /** Whether this citation conflicts with other citations. */
  isConflict?: boolean;
  /** Whether this citation is the designated source of truth. */
  isSourceOfTruth?: boolean;
  /** Whether this is the AI-suggested resolution. */
  isSuggested?: boolean;
  /** Source URL for web-researched citations. */
  sourceUrl?: string;
  /** Show the cited text excerpt (default: true). */
  showCitedText?: boolean;
  className?: string;
}

export function CitationInfoCard({
  citation,
  value,
  filename,
  pageNumber,
  citedText,
  confidence,
  isConflict,
  isSourceOfTruth,
  isSuggested,
  sourceUrl,
  showCitedText = true,
  className,
}: CitationInfoCardProps) {
  const displayValue = value ?? citation.label;
  const displayFilename = filename ?? citation.label;
  const displayPage = pageNumber ?? citation.page;
  const displayCitedText = citedText ?? citation.snippet ?? "";

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {isSourceOfTruth && (
        <div
          className={cn(
            "flex items-center gap-1 text-xs font-medium",
            citationStatusColors.sourceOfTruth.text,
          )}
        >
          <ShieldCheck className="h-3 w-3" />
          Source of truth
        </div>
      )}
      {isConflict && !isSourceOfTruth && (
        <div
          className={cn(
            "flex items-center gap-1 text-xs font-medium",
            citationStatusColors.conflict.text,
          )}
        >
          <AlertTriangle className="h-3 w-3" />
          Mismatch
        </div>
      )}
      {isSuggested && !isSourceOfTruth && !isConflict && (
        <div className="flex items-center gap-1 text-xs font-medium text-violet-600 dark:text-violet-400">
          <Sparkles className="h-3 w-3" />
          Suggested
        </div>
      )}

      <div className="font-medium text-stone-900 dark:text-stone-100">{displayValue}</div>

      {showCitedText && displayCitedText && (
        <p className="text-xs text-stone-500 dark:text-stone-400 line-clamp-2 italic">
          &ldquo;{displayCitedText}&rdquo;
        </p>
      )}

      <div className="flex items-center justify-between gap-2 text-xs text-stone-500 dark:text-stone-400 pt-0.5 border-t border-stone-100 dark:border-stone-700/50 mt-0.5">
        <span className="flex items-center gap-2 min-w-0">
          {sourceUrl ? (
            <>
              <Globe className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{safeHostname(sourceUrl)}</span>
            </>
          ) : (
            <>
              <span className="truncate">{displayFilename}</span>
              {displayPage && <span className="flex-shrink-0">p.{displayPage}</span>}
            </>
          )}
        </span>
        {confidence != null && (
          <span className="flex-shrink-0 flex items-center gap-1">
            <ConfidenceIndicatorRing value={confidence} size="sm" />
            {Math.round(confidence * 100)}%
          </span>
        )}
      </div>
    </div>
  );
}
